import { useEffect, type MutableRefObject } from 'react';
import { useFormRegistry } from 'contexts/FormRegistryProvider';
import { useActionBlocker } from 'contexts/ActionBlockerProvider';
import { safeTrack, trackFormButtonClicked, trackFormSubmitSuccess, trackFormSubmitError } from 'utils/posthog/metrics';
import { getUUID } from 'utils/common';
import { FormValidationException } from 'utils/error/FormValidationException';
import type { DataSchema } from 'types';
import type { TableRef } from 'components/common/Table/Table';

interface UseHeliosFormRegistryProps {
  formNamespace: string;
  templateViewId: string;
  templateId: string;
  dataSchema: DataSchema;
  handleOnSaveEdit: (tableRef: MutableRefObject<TableRef | null>) => Promise<void>;
  tableRef: MutableRefObject<TableRef | null>;
}

export const useHeliosFormRegistry = ({
  formNamespace,
  templateViewId,
  templateId,
  dataSchema,
  handleOnSaveEdit,
  tableRef,
}: UseHeliosFormRegistryProps) => {
  const { register: registerForm, unregister: unregisterForm } = useFormRegistry(formNamespace);
  const { registerBlockingCondition, unregisterBlockingCondition } = useActionBlocker();

  useEffect(() => {
    const id = templateViewId ?? getUUID();

    registerForm(id, {
      data: {
        formIdentifier: templateId,
        templateId,
      },
      api: {
        trigger: async () => {
          if (!tableRef.current) return true;
          return tableRef.current.triggerForm();
        },
        getValues: () => tableRef.current?.getValues() ?? {},
        getDirtyFields: () => tableRef.current?.getFormState().dirtyFields ?? {},
        isDirty: () => !!tableRef.current?.getFormState().isDirty,
        resetSavedForm: () => tableRef.current?.resetSavedForm(),
      },
      dataSchema,
    });
    return () => unregisterForm(id);
  }, [dataSchema, formNamespace, handleOnSaveEdit, registerForm, templateId, templateViewId, unregisterForm]);

  useEffect(() => {
    const id = templateId ?? getUUID();
    registerBlockingCondition('dirtyForm', {
      id,
      isBlocking: () => !!tableRef.current?.getFormState().isDirty,
      unblock: async () => {
        safeTrack(trackFormButtonClicked, {
          button: 'save_rows',
          process: 'record',
          context: 'save_before_leaving',
          area: 'save_before_leaving_modal',
        });

        try {
          await handleOnSaveEdit(tableRef);
          safeTrack(trackFormSubmitSuccess, { form: 'inline_save', process: 'record', step: 'save_rows_success', area: 'save_before_leaving_modal' });
        } catch (e) {
          if (!(e instanceof FormValidationException)) {
            safeTrack(trackFormSubmitError, {
              form: 'inline_save',
              process: 'record',
              step: 'save_rows_error',
              error: 'inline_error',
              area: 'save_before_leaving_modal',
            });
          }
          throw e;
        }
      },
    });
    return () => unregisterBlockingCondition(id);
  }, [templateId, registerBlockingCondition, unregisterBlockingCondition, handleOnSaveEdit]);
};
